
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { TranslateResult } from 'vue-i18n';
import { Localization } from '@/interfaces/models/Localization';
import VRichText from '@/components/shared/form/VRichText.vue';
import { Language as LanguageEnum } from '@/enums/Language';

interface Language {
  key: string;
  value: string | TranslateResult;
}

interface LanguageItem {
  lang: string;
  value: string;
}

@Component({
  components: { VRichText },
})
export default class VLanguageField extends Vue {
  @Prop({ type: String, required: true }) public name!: string;
  @Prop({ type: String, required: true }) public label!: string;
  @Prop({ type: Boolean, default: false }) public required!: boolean;
  @Prop({ required: true }) public value!: Localization;
  @Prop({ type: Array, default: () => [] }) public defaultItems!: string[];
  @Prop({ type: String, default: LanguageEnum.DE }) public defaultLocale!: LanguageEnum;
  @Prop({ type: Array, default: () => [] }) public errors: any;
  @Prop({ type: Boolean, default: false }) public useTextarea!: boolean;
  @Prop({ type: Boolean, default: false }) public useRichText!: boolean;

  public selectedLanguage: string = '';
  public forbiddenFields: string[] = ['createdAt', 'updatedAt', 'deletedAt', '_id'];
  public languages: Language[] = [];
  public items: LanguageItem[] = [];
  public itemsLoaded: boolean = false;
  private internalChange: boolean = false;

  @Watch('value')
  public onValueChange(val: LanguageItem, oldVal: LanguageItem) {
    if (this.internalChange) {
      this.internalChange = false;
      return;
    }

    // @ts-ignore
    if (!oldVal) {
      return;
    }

    if (Object.values(val).join(',') !== Object.values(oldVal).join(',')) {
      this.init();
    }
  }

  public updateItemValue(item: LanguageItem, value: string) {
    item.value = value;
    return this.onChange();
  }

  get orgLanguages() {
    return Object.values(LanguageEnum).map((lang: LanguageEnum) => ({
      key: lang,
      value: this.$t(`languages.${lang}`),
    }));
  }

  public created() {
    this.init();
  }

  public init() {
    this.languages = [];
    this.items = [];
    this.languages.push(...this.orgLanguages);
    this.items.push({ lang: this.defaultLocale, value: '' });
    this.$nextTick(() => {
      if (this.value) {
        this.initDefault();
      }
    });
  }

  public addItem() {
    if (
      this.selectedLanguage !== '' &&
      this.items.filter((item) => {
        return item.lang === this.selectedLanguage;
      }).length === 0
    ) {
      // @ts-ignore
      this.items.push({ lang: this.selectedLanguage, value: '' });

      this.languages = this.languages.filter((item) => {
        return item.key !== this.selectedLanguage;
      });
      this.internalChange = true;
      this.$emit('input', this.mapInput());
    }
  }

  public removeItem(index: number) {
    if (this.items.length > 1) {
      const language = this.orgLanguages.filter((item) => {
        return item.key === this.items[index].lang;
      });
      this.languages.push(...language);
      this.items.splice(index, 1);
      this.internalChange = true;
      this.$emit('input', this.mapInput());
    }
  }

  public mapInput(): Localization {
    const fullObject: Localization = Object.values(LanguageEnum).reduce((acc, lang) => {
      (acc as any)[lang] = '';
      return acc;
    }, {} as Localization);
    const values: Localization = {} as Localization;

    for (const item of this.items) {
      // @ts-ignore
      values[item.lang] = item.value;
    }

    return { ...fullObject, ...values };
  }

  @Emit('input')
  public onChange() {
    this.internalChange = true;
    return this.mapInput();
  }

  public initDefault() {
    const keys: string[] = Object.keys(this.value);
    let edit: boolean = false;
    const existingItems: LanguageItem[] = [];
    for (const key of keys) {
      // @ts-ignore
      const val: string = this.value[key];
      if (this.forbiddenFields.indexOf(key) === -1) {
        edit = true;
        if (keys.indexOf('_id') > -1) {
          if (val !== '' || key === LanguageEnum.DE) {
            existingItems.push({ lang: key, value: val });
          } else {
            continue;
          }
        } else {
          existingItems.push({ lang: key, value: val });
        }
        const languages: Language[] = this.languages.filter((item) => {
          return item.key !== key;
        });
        this.languages = languages;
      }
    }
    if (edit) {
      this.items = [];
    }
    this.items.push(...existingItems);
    this.itemsLoaded = true;
  }
}
